import lodash from 'lodash';
import moment from 'moment';

const PI_ROLE = 'Principal Investigator';

const STAFF_START = 'STAFF_START';
const PI_START = 'PI_START';
const PENDING = 'PENDING';
const COMPLETED = 'COMPLETED';

export function isPITenureOverlappingWithDelegation(piTenure, delegationTenure) {
    const [piStart, piEnd] = piTenure;
    const [delegationStart, delegationEnd] = delegationTenure;

    if (lodash.isNil(piStart) || lodash.isNil(delegationStart)) {
        return false;
    }

    if (!lodash.isNil(piEnd) && !lodash.isNil(delegationStart) && moment(piEnd).isSameOrBefore(delegationStart)) {
        return false;
    }

    if (!lodash.isNil(piStart) && !lodash.isNil(delegationEnd) && moment(piStart).isSameOrAfter(delegationEnd)) {
        return false;
    }

    return true;
}

export const isDelegationTheSameWithDefault = (defaultDelegation, newDelegation) => {
    const fieldsNeedCompare = ['delegatedTasks', 'delegationRole', 'startDate', 'endDate'];

    for (let index = 0; index < fieldsNeedCompare.length; index++) {
        const item = fieldsNeedCompare[index];

        if (!lodash.isEqual(defaultDelegation[item], newDelegation[item])) {
            return false;
        }
    }
    return true;
};

export function delegationsUnderPITenurePredicate(delegations, tenureStartEnd) {
    const [tenureStart, tenureEnd] = tenureStartEnd;
    return lodash.filter(
        delegations,
        ({ role, tasks, startDate, endDate }) =>
            !lodash.isNil(role) &&
            !lodash.isEmpty(tasks) &&
            !lodash.isNil(startDate) &&
            lodash.get(role, 'name') !== PI_ROLE &&
            isPITenureOverlappingWithDelegation([tenureStart, tenureEnd], [startDate, endDate])
    );
}

export const matchPiToDoaList = (principalInvestigator) => {
    return (doa) => doa.id === principalInvestigator.id;
};

// Takes in a status and filter by PI signatures.
export const piSignatureFilter = (status) => {
    return (signature) => {
        const { signatureStatus, signatureType } = signature;
        return signatureStatus === status && signatureType === 'PI_START';
    };
};

// This is how the logic is for the current display for a "completed" vs "outstanding" doa signature
export const validPiSignatureFilter = (signature) => {
    const { signatureType, signatureStatus } = signature;
    const validTypes = signatureStatus === 'COMPLETED' || signatureStatus === 'PENDING';
    return validTypes && signatureType === 'PI_START';
};

// Logic check if pi has pending signatures, if not, its completed.
// Its how its currently work the DOA UI, so we going with it.
export const piHasPendingSignature = (signatures) => {
    const validPiSignatures = signatures.filter(validPiSignatureFilter);
    const pendingSignatures = validPiSignatures.filter(piSignatureFilter('PENDING'));
    return pendingSignatures == 0;
};

/**
 * Check if the current task is in the list of tasks to remove
 */
export const hasTaskToRemovePredicate = (tasksToRemove) => {
    return (id) => tasksToRemove.map((task) => task.id).includes(id);
};

/**
 * returns the signature status of a single delegation as a boolean, accounts for central and Standard DoA types;
 * this doesn't mean that the delegation is fully signed, only that the delegation has a current valid signature;
 *
 * if a delegation has no signatures: false
 * if a delegation is PI and has a complete PI signature: true (centralized or standard)
 * if a delegation is not PI and centralized and has a complete PI signature: true
 * if a delegation is not PI and not centralized and has a complete PI or Staff signature: true
 */
export const delegationSignedPredicate = (centralized, delegation, isPrincipalInvestigator, options) => {
    const hasCompletePrincipalSignature = options.hasCompletePrincipalSignature && !options.hasPendingPrincipalSignature;
    const hasCompleteStaffSignature = options.hasCompleteStaffSignature && !options.hasPendingStaffSignature;

    if (lodash.isEmpty(delegation.signatures)) {
        return false;
    }

    /**
    * If delegation is PI it is doesn't matter if Centralized or not, only PI can sign for PI; 
    */
    if (isPrincipalInvestigator && hasCompletePrincipalSignature) {
        return true;
    }

    /**
     * not PI and centralized; only PI signatures count;
     */
    if (centralized && hasCompletePrincipalSignature) {
        return true;
    }

    /**
     * not PI and not centralized, check if staff has somepleted signature      
     */
    if (!centralized && hasCompleteStaffSignature) {
        return true;
    }

    return false;
};

export const pendingStaffSignaturePredicate = (delegation) => {
    return delegation.signatures.some((s) => s.signatureStatus === PENDING && s.signatureType === STAFF_START);
};

export const completeStaffSignaturePredicate = (delegation) => {
    const hasACompletedSignature = delegation.signatures.some((s) => s.signatureStatus === COMPLETED && s.signatureType === STAFF_START);
    return !pendingStaffSignaturePredicate(delegation) && hasACompletedSignature;
};

export const pendingPrincipalSignaturePredicate = (delegation) => {
    return delegation.signatures.some((s) => s.signatureStatus === PENDING && s.signatureType === PI_START);
};

export const completePrincipalSignaturePredicate = (delegation) => {
    const hasACompletedSignature = delegation.signatures.some((s) => s.signatureStatus === COMPLETED && s.signatureType === PI_START);
    return !pendingPrincipalSignaturePredicate(delegation) && hasACompletedSignature;
};
